/**
 * Parse schema string to layout information object
 * @param {string} customProps - JSON string containing layout info
 * @returns {Object} Layout information object
 */
const parseCustomProps = (customProps) => {
  if (!customProps) {
    return {
      damImageAlignmentBehavior: null,
      damImageWidthBehavior: null,
      descriptionAlignment: null
    };
  }
  try {
    return JSON.parse(customProps);
  } catch (error) {
    return {
      damImageAlignmentBehavior: null,
      damImageWidthBehavior: null,
      descriptionAlignment: null
    };
  }
};

/**
 * Transform source object to DamMedia structure
 * @param {Object} source - Source object (desktop or mobile)
 * @returns {Object|null} DamMedia object or null
 */
const transformToDamMedia = (source) => {
  if (!source) {
    return null;
  }
  return {
    damDownloadedContent: {
      url: source.uri
    },
    imageHotspot: source.hotspot || null,
    id: source.entryId,
    __typename: 'DamMedia'
  };
};

/**
 * Normalizes NewspaperModule data from different sources into a consistent structure
 * Priority: props > slot > component
 *
 * @param {Object} options - Data sources
 * @param {Object} options.slotData - Data from slot query (fusion pattern)
 * @param {Object} options.componentData - Data from component query (legacy pattern)
 * @param {number} options.mediaCollectionIndex - Index of media collection to use (default: 0)
 * @returns {Object} Normalized data with consistent structure
 */
export const normalizeNewspaperData = ({ slotData, componentData, mediaCollectionIndex = 0, isMobileViewport }) => {
  // If slot data exists, transform it to expected structure
  if (slotData?.slot) {
    const slot = slotData.slot;
    const mediaCollection = slot.mediaCollections?.[mediaCollectionIndex];
    const media = mediaCollection?.media?.[0];
    const sourceData = isMobileViewport ? media?.sourceMobile?.[0] : media?.sourceDesktop?.[0];

    // Parse schema to get layout information
    const layoutInfo = parseCustomProps(media?.customProps);

    // Parse description JSON
    const description = media?.richText;

    // Transform sourceDesktop and sourceMobile to DamMedia structure
    const sourceDesktop = media?.sourceDesktop?.[0];
    const sourceMobile = media?.sourceMobile?.[0];

    const desktopImage = transformToDamMedia(sourceDesktop);
    const mobileImage = transformToDamMedia(sourceMobile);

    return {
      source: 'slot',
      id: slot.id,
      entryId: slot.entryId,
      altText: sourceDesktop?.altText || sourceMobile?.altText,
      link: sourceDesktop?.hyperlink || sourceMobile?.hyperlink,
      description,
      damImageAlignmentBehavior: layoutInfo?.damImageAlignmentBehavior,
      damImageWidthBehavior: layoutInfo?.damImageWidthBehavior,
      descriptionAlignment: layoutInfo?.descriptionAlignment,
      desktopImage,
      mobileImage,
      sourceData
    };
  }

  // Fall back to component data (legacy pattern)
  if (componentData?.component) {
    const component = componentData.component;
    return {
      source: 'component',
      id: component.id,
      altText: component.altText,
      link: component.link,
      description: component.description,
      damImageAlignmentBehavior: component.damImageAlignmentBehavior,
      damImageWidthBehavior: component.damImageWidthBehavior,
      descriptionAlignment: component.descriptionAlignment,
      desktopImage: component.desktopImage,
      mobileImage: component.mobileImage
    };
  }

  // No data available
  return null;
};
